import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith, take } from 'rxjs/operators';
import { MatLegacySelect as MatSelect } from '@angular/material/legacy-select';
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { UserInfoService } from 'src/app/shared/services/user-info.service';
import { EventsService } from 'src/app/shared/services/data-api/events.service';
import { FilterListService } from 'src/app/cockpit/main-menu/menu-filters/service/filter-list.service';
import {
  AlertEventList,
  Event,
  EventCategory,
  ScoreOption,
} from '../alerts-setup.models';

export interface EventAlertModel {
  score_type: string;
  threshold: number | string;
  events: Array<{ id: string }>;
}

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-event-alerts',
  templateUrl: './event-alerts.component.html',
  styleUrls: ['./event-alerts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventAlertsComponent implements OnInit {
  @Input() currentAlert: EventAlertModel;
  @Input() scoreEventsTypes: ScoreOption[] = [];

  @Output() fieldSeen = new EventEmitter<void>();

  @ViewChild('selectorEventsScore', { static: false })
  scoreTypeField: MatSelect;
  @ViewChild('inputEvents', { static: false, read: ElementRef })
  eventsInputField: ElementRef;
  @ViewChildren(MatAutocompleteTrigger)
  autocompleteTriggers: QueryList<MatAutocompleteTrigger>;

  eventsControl = new UntypedFormControl();

  eventsList: AlertEventList = { events: [], subEvents: {} };
  events: Event[] = [];
  eventCategories: EventCategory[] = [];

  selectedEvents: Event[] = new Array<Event>();
  selectedEventsCopy: Event[] = new Array<Event>();
  filteredEvents: Observable<Event[]>;
  filteredEventCategories: Observable<EventCategory[]>;

  scoreEvents: string[] = [];
  scoreCategories: string[] = [];
  strictFiltering = false;

  closeAfterSelection = false;
  lastFilter = '';

  private catalogReady = false;

  constructor(
    public userInfo: UserInfoService,
    private filterListService: FilterListService,
    private eventsService: EventsService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit() {
    this.filteredEvents = this.eventsControl.valueChanges.pipe(
      startWith<string | Event[]>(''),
      map((value) => (typeof value === 'string' ? value : this.lastFilter)),
      map((filter) => this.filterEvents(filter)),
    );

    this.filteredEventCategories = this.eventsControl.valueChanges.pipe(
      startWith<string | EventCategory[]>(''),
      map((value) => (typeof value === 'string' ? value : this.lastFilter)),
      map((filter) => this.filterCategories(filter)),
    );

    this.filterListService
      .getEventList()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (eventList) => this.initializeCatalog(eventList),
        error: () => this.initializeCatalog({ events: [], subEvents: {} }),
      });
  }

  private initializeCatalog(eventList: AlertEventList) {
    this.eventsList = JSON.parse(JSON.stringify(eventList));

    const categoryLabels: { [key: string]: string } = {};
    this.eventsList.events.forEach((el) => {
      categoryLabels[el.value] = el.label;
    });

    Object.entries(this.eventsList.subEvents).forEach(([key, value]) => {
      value.forEach((item) => {
        this.events.push(
          new Event(
            item.value,
            item.label,
            categoryLabels[key],
            key.toString(),
          ),
        );
      });
    });

    this.eventsList.events.forEach((event) => {
      this.eventCategories.push(new EventCategory(event.value, event.label));
    });

    this.catalogReady = true;
    this.updateSelectedEvents();
    this.cdr.markForCheck();
  }

  filterEvents(filter: string): Event[] {
    this.lastFilter = filter;
    if (filter) {
      return this.events.filter((option) => {
        return (
          option.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0 ||
          option.category.toLowerCase().indexOf(filter.toLowerCase()) >= 0
        );
      });
    } else {
      return this.events.slice();
    }
  }

  filterCategories(filter: string): EventCategory[] {
    this.lastFilter = filter;
    if (filter) {
      return this.eventCategories.filter((option) => {
        let isMatchingSubevent = false;
        this.eventsList.subEvents[option.id].forEach((el) => {
          if (el.label.toLowerCase().indexOf(filter.toLowerCase()) >= 0) {
            isMatchingSubevent = true;
          }
        });
        return (
          option.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0 ||
          isMatchingSubevent
        );
      });
    } else {
      return this.eventCategories.slice();
    }
  }

  removeEvent(event: Event) {
    this.toggleEventSelection(event);
    if (
      this.filterCategories('').filter(
        (category) => event.categoryId === category.id,
      ).length > 0
    ) {
      this.filterCategories('').filter(
        (category) => event.categoryId === category.id,
      )[0].selected = false;
    }
  }

  displayFnEvents = (): string | undefined => {
    return this.lastFilter;
  };

  unselectGroup(group: EventCategory) {
    group.selected = false;
  }

  toggleEventSelection(event: Event) {
    event.selected = !event.selected;
    if (event.selected) {
      this.selectedEvents.push(event);
    } else {
      const i = this.selectedEvents.findIndex(
        (value) =>
          value.name === event.name && value.category === event.category,
      );
      this.selectedEvents.splice(i, 1);
    }

    this.eventsControl.setValue(this.selectedEvents);
  }

  toggleEventGroupSelection(
    group: EventCategory,
    filtered: Observable<Event[]>,
  ) {
    filtered.pipe(take(1)).subscribe((res) => {
      group.selected = !group.selected;
      if (group.selected) {
        this.eventsList.subEvents[group.id]
          .filter((e) => this.scoreEvents.includes(e.value))
          .forEach((event) => {
            if (
              this.selectedEvents.filter((e) => e.id === event.value).length ===
              0
            ) {
              if (res.filter((e) => e.id === event.value).length !== 0) {
                const eventCopy = res.filter((e) => e.id === event.value)[0];
                eventCopy.selected = true;
                this.selectedEvents.push(eventCopy);
              }
            }
          });
      } else {
        this.eventsList.subEvents[group.id]
          .filter((e) => this.scoreEvents.includes(e.value))
          .forEach((event) => {
            if (
              this.selectedEvents.filter((e) => e.id === event.value).length !==
              0
            ) {
              if (res.filter((e) => e.id === event.value).length !== 0) {
                const eventCopy = res.filter((e) => e.id === event.value)[0];
                eventCopy.selected = false;
                const i = this.selectedEvents.findIndex(
                  (el) => el.id === event.value,
                );
                this.selectedEvents.splice(i, 1);
              }
            }
          });
      }
      this.eventsControl.setValue(this.selectedEvents);
    });
  }

  // `alert` is passed explicitly by the parent: its Input binding only updates
  // on the next change-detection pass, so reading this.currentAlert would be stale.
  updateSelectedEvents(alert: EventAlertModel = this.currentAlert) {
    if (!this.catalogReady) {
      return;
    }
    const scoreType = alert.score_type;
    if (scoreType) {
      this.eventsService
        .getScoreWeights({}, scoreType)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          const res = JSON.parse(JSON.stringify(response));
          if (scoreType === 'sentiment') {
            this.strictFiltering = false;
            this.scoreEvents = JSON.parse(
              JSON.stringify(this.events.map((el) => el.id)),
            );
          } else {
            this.strictFiltering = true;
            this.scoreEvents = res;
          }
          this.scoreCategories = [
            ...new Set(
              this.events
                .filter((el) => this.scoreEvents.includes(el.id))
                .map((el) => el.categoryId),
            ),
          ];
          this.selectedEvents = [];
          this.eventsControl.setValue(this.selectedEvents);
          this.events.forEach((el) => {
            el.selected = false;
          });
          this.eventCategories.forEach((el) => {
            el.selected = false;
          });
          this.events.forEach((el) => {
            if (alert.events.filter((e) => e.id === el.id).length > 0) {
              this.toggleEventSelection(el);
            }
          });
          this.eventsControl.setValue(this.selectedEvents);
          this.cdr.markForCheck();
        });
    } else {
      this.selectedEvents = [];
      this.eventsControl.setValue(this.selectedEvents);
      this.events.forEach((el) => {
        el.selected = false;
      });
      this.eventCategories.forEach((el) => {
        el.selected = false;
      });
      this.scoreEvents = [];
      this.scoreCategories = [];
      this.strictFiltering = false;
      this.cdr.markForCheck();
    }
  }

  updateSelectedEventsWithCopy() {
    this.selectedEvents = [];
    this.eventsControl.setValue(this.selectedEvents);
    this.events.forEach((el) => {
      el.selected = false;
    });
    this.eventCategories.forEach((el) => {
      el.selected = false;
    });
    this.events.forEach((el) => {
      if (this.selectedEventsCopy.filter((e) => e.id === el.id).length > 0) {
        this.toggleEventSelection(el);
      }
    });
    this.eventsControl.setValue(this.selectedEvents);
  }

  onEventsScoreChange(event: { value: string }, clear = false) {
    const changeScore = () => {
      this.eventsService
        .getScoreWeights({}, event.value)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          const res = JSON.parse(JSON.stringify(response));
          if (event.value === 'sentiment') {
            this.strictFiltering = false;
            this.scoreEvents = JSON.parse(
              JSON.stringify(this.events.map((el) => el.id)),
            );
          } else {
            this.strictFiltering = true;
            this.scoreEvents = res;
          }
          this.scoreCategories = [
            ...new Set(
              this.events
                .filter((el) => this.scoreEvents.includes(el.id))
                .map((el) => el.categoryId),
            ),
          ];
          if (clear) {
            this.selectedEvents = [];
            this.eventsControl.setValue(this.selectedEvents);
            this.events.forEach((el) => {
              el.selected = false;
            });
            this.eventCategories.forEach((el) => {
              el.selected = false;
            });
            this.selectedEventsCopy = JSON.parse(
              JSON.stringify(this.selectedEvents),
            );
            this.eventsControl.setValue(this.selectedEvents);
          }
          this.cdr.markForCheck();
        });
    };
    changeScore();
  }

  onAutocompleteOpen() {
    this.closeAfterSelection = false;
    this.selectedEventsCopy = JSON.parse(JSON.stringify(this.selectedEvents));
  }

  onAutocompleteClose() {
    if (!this.closeAfterSelection) {
      this.updateSelectedEventsWithCopy();
    }
    this.closeAfterSelection = false;
  }

  clearEvents() {
    this.onEventsScoreChange({ value: this.currentAlert.score_type }, true);
  }

  selectEvents() {
    this.selectedEventsCopy = JSON.parse(JSON.stringify(this.selectedEvents));
    this.eventsControl.setValue(this.selectedEvents);
    this.closeAfterSelection = true;
    this.closePanels();
  }

  closePanels() {
    this.autocompleteTriggers.forEach((trigger) => {
      if (trigger.panelOpen) {
        trigger.closePanel();
      }
    });
  }

  showEventsFilterLabel() {
    if (this.currentAlert.score_type && this.scoreEventsTypes) {
      return this.scoreEventsTypes
        .filter((el) => el.value === this.currentAlert.score_type)
        .map((el) => el.label)[0];
    } else {
      return '';
    }
  }

  markFieldAsSeen(isVisible: boolean): void {
    if (isVisible) {
      this.fieldSeen.emit();
    }
  }

  sliderLabel(value: number): string {
    if (value === 0) {
      return 'PASS';
    } else if (value === 1) {
      return 'Very Low';
    } else if (value === 2) {
      return 'Low';
    } else if (value === 3) {
      return 'Medium';
    } else if (value === 4) {
      return 'High';
    } else if (value === 5) {
      return 'Very High';
    }

    return `${value}`;
  }

  focusScoreType(): void {
    this.scoreTypeField.focus();
  }

  focusEventsInput(): void {
    this.eventsInputField.nativeElement.focus();
  }
}
